/**
 * Formatting adapters. This module adds display conveniences on top of
 * `@cattle/money`; it never reimplements a formatter. `formatPKR` and
 * `formatPKRShort` live there because the API, the web console and the phone
 * app must agree to the rupee.
 */
import { formatDays, formatPKR, formatPKRShort, todayIso, type IsoDate } from '@cattle/money'

import { t } from './i18n'

export { formatDays, formatPKR, formatPKRShort, todayIso }
export type { IsoDate }

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

/** Splits `YYYY-MM-DD` without going through `Date`, which would shift zone. */
function parts(iso: IsoDate): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split('-').map(Number)
  return { y: y ?? 0, m: m ?? 1, d: d ?? 1 }
}

/** "5 September 2026" */
export function formatDate(iso: IsoDate): string {
  const { y, m, d } = parts(iso)
  return `${d} ${MONTHS[m - 1]} ${y}`
}

/** "5 Sep 2026" — table cells, where the column has to stay narrow. */
export function formatDateShort(iso: IsoDate): string {
  const { y, m, d } = parts(iso)
  return `${d} ${MONTHS[m - 1]?.slice(0, 3)} ${y}`
}

/**
 * "August", given a date in September.
 *
 * Derived from the server's own `today`, never from `new Date()`: the delta on
 * P-01 is defined by the contract as "change against last month", and the month
 * it names has to move with the payload rather than be typed into the copy.
 */
export function previousMonthName(iso: IsoDate): string {
  const { m } = parts(iso)
  return MONTHS[(m + 10) % 12] ?? ''
}

/** "August", given any day in August — the month a billing period covers. */
export function monthName(iso: IsoDate): string {
  return MONTHS[parts(iso).m - 1] ?? ''
}

/**
 * "5 minutes", "45 seconds" — a duration a person set, said back to them.
 *
 * Whole minutes only when it divides; a 30-second auto-lock rounded to "0
 * minutes" would be worse than saying nothing.
 */
export function formatDuration(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) {
    return t('unit.minutesCount', { count: seconds / 60 })
  }
  return t('unit.secondsCount', { count: seconds })
}

/** "Friday, 5 September 2026" — the greeting line at the top of an overview. */
export function formatLongDate(iso: IsoDate): string {
  const { y, m, d } = parts(iso)
  const day = DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
  return `${day}, ${formatDate(iso)}`
}

/**
 * "412 GB", "240 MB", "300 KB" — storage is quoted in whole units, never bytes
 * and never a raw ratio.
 *
 * `null` is "unlimited" and `undefined` is "the server did not say"; both are
 * real answers the contract gives (`limitSchema` is nullable, and a support
 * actor receives no hosting figures at all). Printing "0 MB" for either would
 * be a claim, so they get a word instead.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS A KILOBYTE BRANCH
 * ---------------------------------------------------------------------------
 * A trader's ledger is text and weighs almost nothing; early books hold a
 * handful of photographs and nothing else. Rounding those to "0 MB" tells the
 * reader the book is storing NOTHING, which is a different claim from "not
 * much" — and it is the claim a capacity decision would be made against. A
 * non-zero amount therefore never rounds to zero: it floors at 1 KB.
 *
 * This is the only place that picks a unit. Every screen calls it, and a screen
 * doing the division itself is how `0.0010729385539889336 GB` reached the
 * console in the first place.
 */
export function formatBytes(megabytes: number | null | undefined): string {
  if (megabytes === null) return t('p.usage.unlimited')
  if (megabytes === undefined || Number.isNaN(megabytes)) return t('common.none')

  // A genuine zero is "0 MB". Only an amount that IS nothing may read as none.
  if (megabytes <= 0) return t('unit.mb', { value: 0 })

  if (megabytes >= 1024) {
    const gb = megabytes / 1024
    // One decimal is the most a hosting figure can honestly carry; past ten
    // gigabytes the decimal is noise beside the number in front of it.
    return t('unit.gb', { value: gb >= 10 ? gb.toFixed(0) : gb.toFixed(1).replace(/\.0$/, '') })
  }

  if (megabytes >= 1) return t('unit.mb', { value: Math.round(megabytes) })

  return t('unit.kb', { value: Math.max(1, Math.round(megabytes * 1024)) })
}

/** The same, given gigabytes. Keeps `null` (unlimited) and `undefined` intact. */
export function formatGb(gigabytes: number | null | undefined): string {
  return formatBytes(gigabytes === null || gigabytes === undefined ? gigabytes : gigabytes * 1024)
}

/**
 * `used / limit`, or `undefined` when there is no limit to be a share of.
 * A quota of `null` is unlimited, and "100% of unlimited" is not a number.
 */
export function shareOf(used: number, limit: number | null | undefined): number | undefined {
  if (limit === null || limit === undefined || limit <= 0) return undefined
  return used / limit
}

/**
 * "82%" — never a bare ratio; a percentage always carries its sign.
 *
 * Every ratio in the contract is `.optional()`, because a `PLATFORM_SUPPORT`
 * actor gets usage without revenue and a share-of-revenue is revenue. An
 * absent ratio renders as nothing, not as 0%.
 */
export function formatPercent(fraction: number | null | undefined, decimals = 0): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) {
    return t('common.none')
  }
  return `${(fraction * 100).toFixed(decimals)}%`
}

/** Two initials for an avatar. "Malik Sahib" → "MS", "Farooq" → "FQ". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '??'
  if (words.length === 1) {
    const w = words[0] ?? ''
    return (w.slice(0, 1) + (w.slice(-1) || w.slice(0, 1))).toUpperCase()
  }
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase()
}
